// 🔬 B242 (AI_110) — CATÁLOGO DE CAMPOS PESQUISÁVEIS (camada semântica)
// Fonte ÚNICA dos campos que o construtor de coorte oferece e que o motor
// conta/calcula. As `chave`s são o contrato com o carregador de dados (próximo
// incremento: Firestore → RegistroPesquisa[]) e, no MVP3, com a IA (que recebe
// SÓ este catálogo — nomes de campos — para propor o plano, nunca os valores).
//
// MVP1: conjunto inicial (cresce conforme a necessidade). Campos de TEXTO livre
// (comorbidades, etiologia descritiva) entram p/ filtro "contém", mas não são
// distribuídos na Table 1 (exigiriam NLP — fora do MVP1).
import { CampoPesquisa, OpcaoCampo } from "../types";
import {
  ENXERTO_DOADO,
  VARIACAO_ARTERIAL,
  VARIACAO_PORTAL,
  VARIACAO_BILIAR,
  VEIAS_HEPATICAS,
  SIM_NAO,
  PARECER_PSICOSSOCIAL,
  comoOpcoesPesquisa,
} from "../../../shared/data/opcoesDoador";
import {
  TITULOS_AUTOANTICORPO,
  OPCOES_ANCA,
} from "../../sorologias/services/normalizarSorologia";

// B247: opções padronizadas das sorologias qualitativas (fonte: normalizarSorologia).
const OPCOES_SOROLOGIA: OpcaoCampo[] = [
  { valor: "Reagente", label: "Reagente" },
  { valor: "Não Reagente", label: "Não Reagente" },
  { valor: "Indeterminado", label: "Indeterminado" },
];
const OPCOES_TITULO: OpcaoCampo[] = TITULOS_AUTOANTICORPO.map((t) => ({ valor: t, label: t }));
const OPCOES_ANCA_CAMPO: OpcaoCampo[] = OPCOES_ANCA.map((o) => ({ valor: o, label: o }));
const OPCOES_CARGA_STATUS: OpcaoCampo[] = [
  { valor: "Detectável", label: "Detectável" },
  { valor: "Não detectado", label: "Não detectado" },
  { valor: "Detectável (não quantificável)", label: "Detectável (não quantificável)" },
];

export const CAMPOS_PESQUISA: CampoPesquisa[] = [
  // ── Linha de cuidado (1º filtro recomendado) ──
  {
    // Grupo derivado de `tipoAtendimento`, com pâncreas e rim-pâncreas no MESMO
    // grupo (decisão de Leonardo, AI_111). É o primeiro filtro recomendado:
    // garante que, ao isolar "pré-transplante", a coorte seja de um grupo
    // específico (ex.: fígado), e não mistura de linhas de cuidado.
    chave: "grupo",
    label: "Grupo (linha de cuidado)",
    tipo: "categorico",
    grupo: "Linha de cuidado",
    opcoes: [
      { valor: "ambulatorial", label: "Ambulatório (hepatologia)" },
      { valor: "figado", label: "Fígado" },
      { valor: "rim", label: "Rim" },
      { valor: "pancreas", label: "Pâncreas (inclui rim-pâncreas)" },
      // B351 (AI_152) — o doador vivo é uma linha de cuidado PRÓPRIA: filtrar por
      // ele isola a coorte de quem doa, que não se mistura com a de quem recebe.
      { valor: "doador_figado", label: "Doador de fígado (intervivos)" },
    ],
  },

  // ── Demografia ──
  { chave: "idade", label: "Idade", tipo: "numerico", unidade: "anos", grupo: "Demografia" },
  {
    chave: "sexo",
    label: "Sexo",
    tipo: "categorico",
    grupo: "Demografia",
    opcoes: [
      { valor: "M", label: "Masculino" },
      { valor: "F", label: "Feminino" },
    ],
  },
  {
    chave: "tipoSanguineo",
    label: "Tipo sanguíneo (ABO/Rh)",
    tipo: "categorico",
    grupo: "Demografia",
    opcoes: ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"].map((t) => ({ valor: t, label: t })),
  },

  // ── Transplante / status ──
  {
    // Nível alto: pré (total) × pós. Use este para "pré-transplante total"
    // (inscritos + não inscritos juntos).
    chave: "faseTransplante",
    label: "Transplante (pré × pós)",
    tipo: "categorico",
    grupo: "Transplante",
    opcoes: [
      { valor: "pre", label: "Pré-transplante (total)" },
      { valor: "pos", label: "Pós-transplante" },
    ],
  },
  {
    // Detalhe do pré: separa inscrito (tem RGCT) de não inscrito.
    chave: "statusTransplante",
    label: "Status de transplante (detalhe)",
    tipo: "categorico",
    grupo: "Transplante",
    opcoes: [
      { valor: "pre", label: "Pré-Tx — não inscrito" },
      { valor: "lista", label: "Pré-Tx — inscrito" },
      { valor: "pos", label: "Pós-transplante" },
    ],
  },
  {
    chave: "statusPaciente",
    label: "Situação do paciente",
    tipo: "categorico",
    grupo: "Transplante",
    opcoes: [
      { valor: "ativo", label: "Ativo" },
      { valor: "obito", label: "Óbito" },
      { valor: "perda_seguimento", label: "Perda de seguimento" },
      { valor: "retirado_lista", label: "Retirado da lista" },
      { valor: "transferido", label: "Transferido" },
      { valor: "alta", label: "Alta" },
    ],
  },
  { chave: "diasPosTx", label: "Dias pós-transplante", tipo: "numerico", unidade: "dias", grupo: "Transplante" },
  { chave: "situacaoEspecial", label: "Situação especial (pontuação)", tipo: "booleano", grupo: "Transplante" },
  { chave: "etiologia", label: "Etiologia / causa da cirrose", tipo: "texto", grupo: "Transplante" },

  // ── Hepatocarcinoma (CHC) — marcação estruturada (B248) ──
  { chave: "chc", label: "CHC (hepatocarcinoma)", tipo: "booleano", grupo: "CHC" },
  {
    chave: "hccBCLC",
    label: "Estádio BCLC",
    tipo: "categorico",
    grupo: "CHC",
    opcoes: [
      { valor: "0", label: "0 (muito precoce)" },
      { valor: "A", label: "A (precoce)" },
      { valor: "B", label: "B (intermediário)" },
      { valor: "C", label: "C (avançado)" },
      { valor: "D", label: "D (terminal)" },
    ],
  },
  {
    chave: "hccMilao",
    label: "Critério de Milão",
    tipo: "categorico",
    grupo: "CHC",
    opcoes: [
      { valor: "Dentro", label: "Dentro de Milão" },
      { valor: "Fora", label: "Fora de Milão" },
    ],
  },
  { chave: "hccTerapiaLocorregional", label: "Terapia prévia do CHC", tipo: "booleano", grupo: "CHC" },

  // ── Gravidade (pré-Tx) ──
  { chave: "meld", label: "MELD", tipo: "numerico", grupo: "Gravidade" },
  { chave: "meldNa", label: "MELD-Na", tipo: "numerico", grupo: "Gravidade" },
  {
    // O sistema armazena a CLASSE (A/B/C), não os pontos — o carregador deriva a
    // classe da string "B (8pts)" do exame ou dos pontos do score (B242/AI_111).
    chave: "childPugh",
    label: "Child-Pugh (classe)",
    tipo: "categorico",
    grupo: "Gravidade",
    opcoes: [
      { valor: "A", label: "Classe A" },
      { valor: "B", label: "Classe B" },
      { valor: "C", label: "Classe C" },
    ],
  },
  { chave: "ascite", label: "Ascite", tipo: "booleano", grupo: "Gravidade" },
  { chave: "encefalopatia", label: "Encefalopatia", tipo: "booleano", grupo: "Gravidade" },

  // ── Hipertensão portal / descompensação (B242 MVP3a, AI_112) ──
  { chave: "varizesEsofago", label: "Varizes de esôfago", tipo: "booleano", grupo: "Hipertensão portal" },
  { chave: "varizesGastrica", label: "Varizes gástricas", tipo: "booleano", grupo: "Hipertensão portal" },
  { chave: "hdaPrevia", label: "HDA prévia (hemorragia digestiva)", tipo: "booleano", grupo: "Hipertensão portal" },
  { chave: "pbe", label: "PBE (peritonite bacteriana espontânea)", tipo: "booleano", grupo: "Hipertensão portal" },
  { chave: "empiemaBacterianoEspontaneo", label: "Empiema pleural bacteriano espontâneo", tipo: "booleano", grupo: "Hipertensão portal" },
  { chave: "colangiteRepeticao", label: "Colangite bacteriana de repetição", tipo: "booleano", grupo: "Hipertensão portal" },
  { chave: "colangioTransparietoHepatica", label: "Colangiografia/drenagem trans-parieto-hepática (CTPH)", tipo: "booleano", grupo: "Hipertensão portal" },
  { chave: "shr", label: "Síndrome hepatorrenal", tipo: "booleano", grupo: "Hipertensão portal" },
  { chave: "dialise", label: "Diálise (substituição renal)", tipo: "booleano", grupo: "Hipertensão portal" },
  { chave: "hidrotorax", label: "Hidrotórax hepático", tipo: "booleano", grupo: "Hipertensão portal" },
  { chave: "hipertensaoPortopulmonar", label: "Hipertensão portopulmonar", tipo: "booleano", grupo: "Hipertensão portal" },
  { chave: "shdHepatopulmonar", label: "Síndrome hepatopulmonar", tipo: "booleano", grupo: "Hipertensão portal" },
  { chave: "pruridoIntratavel", label: "Prurido intratável", tipo: "booleano", grupo: "Hipertensão portal" },
  { chave: "insuficienciaHepaticaAguda", label: "Insuficiência hepática aguda (fulminante)", tipo: "booleano", grupo: "Hipertensão portal" },
  { chave: "aclf", label: "ACLF (agudização sobre cirrose)", tipo: "booleano", grupo: "Hipertensão portal" },
  { chave: "sarcopenia", label: "Sarcopenia", tipo: "booleano", grupo: "Hipertensão portal" },
  { chave: "paracentese", label: "Paracentese (já realizada)", tipo: "booleano", grupo: "Hipertensão portal" },
  { chave: "ligaduraElastica", label: "Ligadura elástica de varizes", tipo: "booleano", grupo: "Hipertensão portal" },
  { chave: "tips", label: "TIPS (shunt portossistêmico)", tipo: "booleano", grupo: "Hipertensão portal" },

  // ── Laboratório (coleta mais recente) ──
  { chave: "hemoglobina", label: "Hemoglobina", tipo: "numerico", unidade: "g/dL", grupo: "Laboratório" },
  { chave: "plaquetas", label: "Plaquetas", tipo: "numerico", unidade: "/µL", grupo: "Laboratório" },
  { chave: "creatinina", label: "Creatinina", tipo: "numerico", unidade: "mg/dL", grupo: "Laboratório" },
  { chave: "bilirrubinaTotal", label: "Bilirrubina total", tipo: "numerico", unidade: "mg/dL", grupo: "Laboratório" },
  { chave: "albumina", label: "Albumina", tipo: "numerico", unidade: "g/dL", grupo: "Laboratório" },
  { chave: "inr", label: "INR", tipo: "numerico", grupo: "Laboratório" },
  // Função hepática / colestase (B242 MVP3a, AI_112)
  { chave: "tgo", label: "TGO/AST", tipo: "numerico", unidade: "U/L", grupo: "Laboratório" },
  { chave: "tgp", label: "TGP/ALT", tipo: "numerico", unidade: "U/L", grupo: "Laboratório" },
  { chave: "ggt", label: "Gama-GT (GGT)", tipo: "numerico", unidade: "U/L", grupo: "Laboratório" },
  { chave: "fosfataseAlcalina", label: "Fosfatase alcalina", tipo: "numerico", unidade: "U/L", grupo: "Laboratório" },
  { chave: "bilirrubinaDireta", label: "Bilirrubina direta", tipo: "numerico", unidade: "mg/dL", grupo: "Laboratório" },
  // Hemograma / renal / eletrólitos
  { chave: "leucocitos", label: "Leucócitos", tipo: "numerico", unidade: "/mm³", grupo: "Laboratório" },
  { chave: "sodio", label: "Sódio", tipo: "numerico", unidade: "mEq/L", grupo: "Laboratório" },
  { chave: "potassio", label: "Potássio", tipo: "numerico", unidade: "mEq/L", grupo: "Laboratório" },
  { chave: "calcio", label: "Cálcio", tipo: "numerico", unidade: "mg/dL", grupo: "Laboratório" },
  { chave: "ureia", label: "Ureia", tipo: "numerico", unidade: "mg/dL", grupo: "Laboratório" },
  // Metabólico / inflamação / pâncreas / tireoide / lipídios
  { chave: "glicemia", label: "Glicemia", tipo: "numerico", unidade: "mg/dL", grupo: "Laboratório" },
  { chave: "hemoglobinaGlicada", label: "Hemoglobina glicada (HbA1c)", tipo: "numerico", unidade: "%", grupo: "Laboratório" },
  { chave: "pcr", label: "PCR (proteína C reativa)", tipo: "numerico", unidade: "mg/L", grupo: "Laboratório" },
  { chave: "amilase", label: "Amilase", tipo: "numerico", unidade: "U/L", grupo: "Laboratório" },
  { chave: "lipase", label: "Lipase", tipo: "numerico", unidade: "U/L", grupo: "Laboratório" },
  { chave: "tsh", label: "TSH", tipo: "numerico", unidade: "µUI/mL", grupo: "Laboratório" },
  { chave: "colesterolTotal", label: "Colesterol total", tipo: "numerico", unidade: "mg/dL", grupo: "Laboratório" },
  { chave: "triglicerides", label: "Triglicérides", tipo: "numerico", unidade: "mg/dL", grupo: "Laboratório" },
  { chave: "hdl", label: "HDL", tipo: "numerico", unidade: "mg/dL", grupo: "Laboratório" },
  { chave: "ldl", label: "LDL", tipo: "numerico", unidade: "mg/dL", grupo: "Laboratório" },
  // Imunossupressores (níveis séricos pós-Tx)
  { chave: "tacrolimus", label: "Nível de tacrolimus", tipo: "numerico", unidade: "ng/mL", grupo: "Laboratório" },
  { chave: "everolimus", label: "Nível de everolimo", tipo: "numerico", unidade: "ng/mL", grupo: "Laboratório" },
  { chave: "sirolimus", label: "Nível de sirolimo", tipo: "numerico", unidade: "ng/mL", grupo: "Laboratório" },

  // ── Marcadores tumorais / etiológicos (último valor por campo) ──
  { chave: "afp", label: "Alfafetoproteína (AFP)", tipo: "numerico", unidade: "ng/mL", grupo: "Marcadores" },
  { chave: "ca199", label: "CA 19-9", tipo: "numerico", unidade: "U/mL", grupo: "Marcadores" },
  { chave: "cea", label: "CEA", tipo: "numerico", unidade: "ng/mL", grupo: "Marcadores" },
  { chave: "ferritina", label: "Ferritina", tipo: "numerico", unidade: "ng/mL", grupo: "Marcadores" },
  { chave: "ferro", label: "Ferro sérico", tipo: "numerico", unidade: "µg/dL", grupo: "Marcadores" },
  { chave: "saturacaoTransferrina", label: "Saturação de transferrina", tipo: "numerico", unidade: "%", grupo: "Marcadores" },
  { chave: "ceruloplasmina", label: "Ceruloplasmina", tipo: "numerico", unidade: "mg/dL", grupo: "Marcadores" },
  { chave: "alfa1Antitripsina", label: "Alfa-1 antitripsina", tipo: "numerico", unidade: "mg/dL", grupo: "Marcadores" },
  { chave: "clearanceCreatinina", label: "Clearance de creatinina (total)", tipo: "numerico", unidade: "mL/min", grupo: "Marcadores" },
  { chave: "clearanceCreatininaCorrigido", label: "Clearance de creatinina (corrigido 1,73m²)", tipo: "numerico", unidade: "mL/min/1,73m²", grupo: "Marcadores" },
  { chave: "proteinuria24h", label: "Proteinúria de 24h", tipo: "numerico", unidade: "mg/24h", grupo: "Marcadores" },
  { chave: "sodioUrinarioAmostra", label: "Sódio urinário (amostra isolada)", tipo: "numerico", unidade: "mEq/L", grupo: "Marcadores" },
  { chave: "sodioUrinario24h", label: "Sódio urinário (24h)", tipo: "numerico", unidade: "mEq/24h", grupo: "Marcadores" },
  { chave: "iggSerico", label: "IgG sérica", tipo: "numerico", unidade: "mg/dL", grupo: "Marcadores" },
  { chave: "igg4", label: "IgG4", tipo: "numerico", unidade: "mg/dL", grupo: "Marcadores" },

  // ── Sorologias (status padronizado B247: Reagente/Não Reagente/Indeterminado) ──
  { chave: "hbsag", label: "HBsAg", tipo: "categorico", grupo: "Sorologias", opcoes: OPCOES_SOROLOGIA },
  { chave: "antiHBs", label: "Anti-HBs", tipo: "categorico", grupo: "Sorologias", opcoes: OPCOES_SOROLOGIA },
  { chave: "antiHBcIgG", label: "Anti-HBc IgG", tipo: "categorico", grupo: "Sorologias", opcoes: OPCOES_SOROLOGIA },
  { chave: "antiHCV", label: "Anti-HCV", tipo: "categorico", grupo: "Sorologias", opcoes: OPCOES_SOROLOGIA },
  { chave: "antiHIV", label: "Anti-HIV", tipo: "categorico", grupo: "Sorologias", opcoes: OPCOES_SOROLOGIA },
  // Hepatite B (detalhe), A, Delta e rastreio pré-Tx (B242 MVP3a, AI_112)
  { chave: "hbeag", label: "HBeAg", tipo: "categorico", grupo: "Sorologias", opcoes: OPCOES_SOROLOGIA },
  { chave: "antiHBe", label: "Anti-HBe", tipo: "categorico", grupo: "Sorologias", opcoes: OPCOES_SOROLOGIA },
  { chave: "antiHBcIgM", label: "Anti-HBc IgM", tipo: "categorico", grupo: "Sorologias", opcoes: OPCOES_SOROLOGIA },
  { chave: "antiHAVIgG", label: "Anti-HAV IgG (imunidade)", tipo: "categorico", grupo: "Sorologias", opcoes: OPCOES_SOROLOGIA },
  { chave: "antiHDV", label: "Anti-HDV (Delta)", tipo: "categorico", grupo: "Sorologias", opcoes: OPCOES_SOROLOGIA },
  { chave: "cmvIgG", label: "CMV IgG", tipo: "categorico", grupo: "Sorologias", opcoes: OPCOES_SOROLOGIA },
  { chave: "ebvIgG", label: "EBV IgG", tipo: "categorico", grupo: "Sorologias", opcoes: OPCOES_SOROLOGIA },
  { chave: "toxoIgG", label: "Toxoplasmose IgG", tipo: "categorico", grupo: "Sorologias", opcoes: OPCOES_SOROLOGIA },
  { chave: "vdrl", label: "VDRL", tipo: "categorico", grupo: "Sorologias", opcoes: OPCOES_SOROLOGIA },
  { chave: "chagasIgG", label: "Chagas IgG", tipo: "categorico", grupo: "Sorologias", opcoes: OPCOES_SOROLOGIA },
  { chave: "htlv", label: "HTLV", tipo: "categorico", grupo: "Sorologias", opcoes: OPCOES_SOROLOGIA },
  { chave: "estrongiloidesIgG", label: "Estrongiloides IgG", tipo: "categorico", grupo: "Sorologias", opcoes: OPCOES_SOROLOGIA },
  { chave: "igraTB", label: "IGRA / Quantiferon-TB", tipo: "categorico", grupo: "Sorologias", opcoes: OPCOES_SOROLOGIA },

  // ── Autoanticorpos (B247: título/padrão padronizados) ──
  { chave: "fan", label: "FAN", tipo: "categorico", grupo: "Autoanticorpos", opcoes: OPCOES_TITULO },
  { chave: "antiMusculoLiso", label: "Anti-músculo liso (ASMA)", tipo: "categorico", grupo: "Autoanticorpos", opcoes: OPCOES_TITULO },
  { chave: "antiMitocondria", label: "Anti-mitocôndria (AMA)", tipo: "categorico", grupo: "Autoanticorpos", opcoes: OPCOES_TITULO },
  { chave: "antiLKM", label: "Anti-LKM1", tipo: "categorico", grupo: "Autoanticorpos", opcoes: OPCOES_TITULO },
  { chave: "anca", label: "ANCA", tipo: "categorico", grupo: "Autoanticorpos", opcoes: OPCOES_ANCA_CAMPO },

  // ── Cargas virais (B247: número só dos detectáveis + status; nunca 0) ──
  { chave: "hcvRNAValor", label: "HCV-RNA (carga)", tipo: "numerico", unidade: "UI/mL", grupo: "Carga viral" },
  { chave: "hcvRNAStatus", label: "HCV-RNA (status)", tipo: "categorico", grupo: "Carga viral", opcoes: OPCOES_CARGA_STATUS },
  { chave: "hbvDNAValor", label: "HBV-DNA (carga)", tipo: "numerico", unidade: "UI/mL", grupo: "Carga viral" },
  { chave: "hbvDNAStatus", label: "HBV-DNA (status)", tipo: "categorico", grupo: "Carga viral", opcoes: OPCOES_CARGA_STATUS },
  { chave: "cmvPCRValor", label: "CMV-PCR (carga)", tipo: "numerico", grupo: "Carga viral" },
  { chave: "cmvPCRStatus", label: "CMV-PCR (status)", tipo: "categorico", grupo: "Carga viral", opcoes: OPCOES_CARGA_STATUS },

  // ── Comorbidades (texto livre) ──
  { chave: "comorbidades", label: "Comorbidades (texto)", tipo: "texto", grupo: "Comorbidades" },

  // ── Doador vivo de fígado (B351, AI_152) ──
  // Só fazem sentido na coorte de doadores; nos demais pacientes vêm nulos e a aba
  // trata como faltante. As opções vêm da MESMA fonte que a aba de Avaliação do
  // Doador grava (opcoesDoador.ts) — se as duas listas divergissem, o filtro
  // deixaria de encontrar os pacientes sem erro nenhum.
  { chave: "doadorEnxerto", label: "Enxerto a ser doado", tipo: "categorico", grupo: "Doador vivo", opcoes: comoOpcoesPesquisa(ENXERTO_DOADO) },
  { chave: "doadorGRWR", label: "GRWR", tipo: "numerico", unidade: "%", grupo: "Doador vivo" },
  { chave: "doadorRemanescente", label: "Remanescente do doador", tipo: "numerico", unidade: "%", grupo: "Doador vivo" },
  { chave: "doadorIMC", label: "IMC do doador", tipo: "numerico", unidade: "kg/m²", grupo: "Doador vivo" },
  { chave: "doadorEsteatose", label: "Esteatose hepática", tipo: "numerico", unidade: "%", grupo: "Doador vivo" },
  { chave: "doadorVariacaoArterial", label: "Variação arterial (Michels)", tipo: "categorico", grupo: "Doador vivo", opcoes: comoOpcoesPesquisa(VARIACAO_ARTERIAL) },
  { chave: "doadorVariacaoPortal", label: "Variação portal", tipo: "categorico", grupo: "Doador vivo", opcoes: comoOpcoesPesquisa(VARIACAO_PORTAL) },
  { chave: "doadorVariacaoBiliar", label: "Variação biliar", tipo: "categorico", grupo: "Doador vivo", opcoes: comoOpcoesPesquisa(VARIACAO_BILIAR) },
  { chave: "doadorVeiasHepaticas", label: "Veias hepáticas — padrão", tipo: "categorico", grupo: "Doador vivo", opcoes: comoOpcoesPesquisa(VEIAS_HEPATICAS) },
  { chave: "doadorIncluiVHM", label: "Inclui veia hepática média", tipo: "categorico", grupo: "Doador vivo", opcoes: comoOpcoesPesquisa(SIM_NAO) },
  { chave: "doadorPsicossocial", label: "Parecer psicossocial", tipo: "categorico", grupo: "Doador vivo", opcoes: comoOpcoesPesquisa(PARECER_PSICOSSOCIAL) },
  { chave: "doadorReceptorVinculado", label: "Receptor vinculado", tipo: "booleano", grupo: "Doador vivo" },
];

// Índice por chave (consulta O(1)).
export const CAMPO_POR_CHAVE: Record<string, CampoPesquisa> = Object.fromEntries(
  CAMPOS_PESQUISA.map((c) => [c.chave, c])
);
